import React, { useCallback, useState } from "react";
import { Alert, CircularProgress, Snackbar } from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { useBillingPlans } from "../hooks/useBillingPlans";
import { isAdmin } from "../../../shared/utils/roles";

const CheckIcon = ({ className = "w-4 h-4" }) => (
  <svg
    width="12"
    height="15"
    viewBox="0 0 10 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M0.46875 5.15625C0.46875 5.15625 1.40625 5.15625 2.65625 7.34375C2.65625 7.34375 6.13063 1.61437 9.21875 0.46875"
      stroke="#2D3748"
      strokeWidth="0.9375"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function PlansGrid() {
  const currentUser = useSelector(selectCurrentUser);
  const canChangePlan = isAdmin(currentUser?.user_role);

  const [tooltipVisible, setTooltipVisible] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const {
    billingCycle,
    isLoadingPlans,
    isUpdatingPlanId,
    plansError,
    plansData,
    getDisplayedPrice,
    getButtonText,
    setBillingCycle,
    handleChangePlan,
  } = useBillingPlans();

  const showToast = useCallback((message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleChangePlanClick = useCallback(
    async (plan) => {
      const result = await handleChangePlan(plan);
      showToast(result.message, result.success ? "success" : "error");
    },
    [handleChangePlan, showToast],
  );

  return (
    <div className="mt-6 bg-white rounded-2xl shadow-sm max-sm:px-4 p-6 pb-10 sm:pb-14 md:pb-20 border border-line sm:mx-2">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between sm:gap-0 gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">
            Subscription Plans
          </h2>
          <p className="text-sm text-mutedLight">
            Choose the plan that fits your needs
          </p>
        </div>

        <div className="flex items-center gap-2 bg-primary p-2 rounded-md">
          <button
            onClick={() => setBillingCycle("monthly")}
            className={`px-5 py-1 rounded-sm text-xs font-bold ${
              billingCycle === "monthly"
                ? "bg-white text-primary"
                : "text-white"
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle("annual")}
            className={`px-5 py-1 rounded-sm text-xs font-bold ${
              billingCycle === "annual" ? "bg-white text-primary" : "text-white"
            }`}
          >
            Annual
          </button>
        </div>
      </div>

      <div>
        {plansError ? (
          <div className="mt-4 rounded-md border border-danger/20 bg-danger/5 px-3 py-2 text-sm text-danger">
            {plansError}
          </div>
        ) : null}

        {isLoadingPlans && (
          <span className="mt-4 text-sm text-muted flex items-center gap-1.5">
            <CircularProgress
              size={14}
              thickness={5}
              sx={{ color: "var(--color-primary, #002841)" }}
            />
            Loading subscription plans...
          </span>
        )}

        <div className="w-full grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 2xl:grid-cols-5 gap-4 mt-8">
          {plansData.map((plan) =>
            (() => {
              const isCustomPlanCard =
                plan.isCustom ||
                plan.id === "custom" ||
                Number(plan.id) === 5 ||
                String(plan.name || "")
                  .trim()
                  .toLowerCase() === "custom";

              return (
                <div
                  key={plan.id}
                  className="flex-0 w-full md:min-h-[480px] lg:min-h-[575px] border border-line rounded-xl md:rounded-3xl p-4 sm:p-5 sm:py-6 relative bg-surface/30 flex flex-col justify-between"
                >
                  {plan.current && (
                    <div className="absolute left-1/2 -translate-x-1/2 top-0 sm:-top-3 z-10">
                      <div className="bg-primary text-white text-xs px-3 py-1 max-sm:rounded-b-md sm:rounded-sm w-fit whitespace-nowrap">
                        CURRENT PLAN
                      </div>
                    </div>
                  )}
                  <div>
                    {!isCustomPlanCard && (
                      <div className="text-lg text-black font-semibold">
                        {plan.name}
                      </div>
                    )}

                    <div className=" mt-2 sm:mt-4">
                      <div className="text-2xl sm:text-3xl lg:text-4xl xl:text-[40px] font-bold text-black">
                        {getDisplayedPrice(plan) ? (
                          <>
                            ${getDisplayedPrice(plan)}/
                            <span className="text-lg font-bold text-black">{`${billingCycle === "monthly" ? "mo" : "annual"}`}</span>
                          </>
                        ) : (
                          <span className="flex items-center">Custom</span>
                        )}
                      </div>
                      <div
                        className={`text-xs text-subtle ${!getDisplayedPrice(plan) ? "my-6" : "mt-1"}`}
                      >
                        {plan.voicemails}
                      </div>
                    </div>

                    <hr className="my-4 border-line" />

                    <ul className="text-sm text-black space-y-4 min-h-[120px] my-12">
                      {plan.features.map((feature, i) => {
                        const parts = feature.split("*");
                        return (
                          <li key={i} className="flex items-start gap-2">
                            <div>
                              <CheckIcon fontSize="small" />
                            </div>
                            <div>
                              {parts.length > 1 ? (
                                <>
                                  {parts[0]}
                                  <span className="text-red-600">*</span>
                                  {parts.slice(1).join("*")}
                                </>
                              ) : (
                                feature
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                  <div className="mt-4">
                      {plan.current ? (
                        <button
                          disabled={!canChangePlan}
                          className="w-full bg-transparent border border-primary text-content rounded-md py-2 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {plan.cta}
                        </button>
                      ) : isCustomPlanCard ? (
                        <div className="relative">
                          {tooltipVisible === `custom-${plan.id}` && (
                            <div
                              id="contact-sales-tooltip"
                              role="tooltip"
                              className="absolute left-1/2 -translate-x-1/2 bottom-full mb-3 w-[200px] bg-primary text-white rounded-xl p-2 text-[9px] shadow-lg z-20 "
                            >
                              <div className="leading-relaxed">
                                Choose this option if your voicemail volume
                                exceeds standard plans. Our team will provide a
                                personalized quote
                              </div>
                              <div
                                className="w-3 h-3 bg-primary absolute -bottom-1 left-1/2 -translate-x-1/2 rotate-45"
                                aria-hidden
                              />
                            </div>
                          )}

                          <button
                            aria-describedby="contact-sales-tooltip"
                            onMouseEnter={() =>
                              setTooltipVisible(`custom-${plan.id}`)
                            }
                            onMouseLeave={() => setTooltipVisible(null)}
                            onFocus={() =>
                              setTooltipVisible(`custom-${plan.id}`)
                            }
                            onBlur={() => setTooltipVisible(null)}
                            disabled={!canChangePlan}
                            className="w-full bg-primary text-white rounded-md py-2 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {plan.cta}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleChangePlanClick(plan)}
                              disabled={
                                !canChangePlan ||
                                isUpdatingPlanId === plan.id || isLoadingPlans
                              }
                          className="w-full bg-primary text-white rounded-md py-2 text-xs sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isUpdatingPlanId === plan.id
                            ? "UPDATING..."
                            : getButtonText(plan)}
                        </button>
                      )}
                    </div>
                </div>
              );
            })(),
          )}
        </div>

        {/* Info bubble */}
        <div className="mt-6 md:mt-8 lg:mt-10 flex items-start gap-1 sm:gap-3 bg-primary text-white rounded-lg p-2 sm:p-4">
          <div className="flex-shrink-0 sm:mt-0.5">
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="10"
                cy="10"
                r="9"
                stroke="currentColor"
                strokeWidth="1.5"
              />
              <path
                d="M10 14V10M10 6H10.01"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <p className="text-xs sm:text-sm leading-relaxed">
            Monthly fees for voicemail boxes purchased in "Number Management"
            module are additional.
          </p>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
